import React, { useState, useEffect } from "react";
import { draftsApi } from "../../services/api/draftsApi";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import "./DraftsPage.css";

const DraftsPage = () => {
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("drafts"); // 'drafts' or 'scheduled'

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const response = await draftsApi.getDrafts({ includeScheduled: true });

      // Separate drafts and scheduled
      const draftsList = response.data.data.filter((d) => !d.scheduledFor);
      const scheduledList = response.data.data.filter((d) => d.scheduledFor);

      setDrafts(draftsList);
      setScheduled(scheduledList);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (draftId) => {
    if (!window.confirm("Publish this draft now?")) return;

    try {
      await draftsApi.publishDraft(draftId);
      alert("Draft published!");
      loadDrafts();
    } catch (error) {
      alert("Failed to publish draft");
    }
  };

  const handleDelete = async (draftId) => {
    if (!window.confirm("Delete this draft?")) return;

    try {
      await draftsApi.deleteDraft(draftId);
      loadDrafts();
    } catch (error) {
      alert("Failed to delete draft");
    }
  };

  const DraftCard = ({ draft }) => (
    <div className="draft-card">
      <div className="draft-header">
        <span className="draft-type">{draft.postType}</span>
        <span className="draft-date">
          {formatDistanceToNow(new Date(draft.updatedAt), { addSuffix: true })}
        </span>
      </div>

      <div className="draft-content">
        {draft.postType === "question" && draft.question?.title && (
          <h3>{draft.question.title}</h3>
        )}
        {draft.postType === "collaboration" && draft.collaboration?.title && (
          <h3>{draft.collaboration.title}</h3>
        )}
        {draft.postType === "poll" && draft.poll?.question && (
          <h3>{draft.poll.question}</h3>
        )}
        {draft.postType === "milestone" && draft.milestone?.title && (
          <h3>{draft.milestone.title}</h3>
        )}
        <p className="draft-preview">
          {draft.content?.substring(0, 150)}
          {draft.content?.length > 150 && "..."}
        </p>
      </div>

      {draft.scheduledFor && (
        <div className="scheduled-info">
          📅 Scheduled for: {new Date(draft.scheduledFor).toLocaleString()}
        </div>
      )}

      <div className="draft-actions">
        <Link to={`/drafts/${draft._id}/edit`} className="edit-btn">
          Edit
        </Link>
        <button onClick={() => handlePublish(draft._id)}>Publish Now</button>
        <button onClick={() => handleDelete(draft._id)} className="delete-btn">
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="drafts-page">
      <header>
        <h1>My Drafts</h1>
        <Link to="/feed" className="create-btn">
          Create New Post
        </Link>
      </header>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === "drafts" ? "active" : ""}
          onClick={() => setActiveTab("drafts")}
        >
          Drafts ({drafts.length})
        </button>
        <button
          className={activeTab === "scheduled" ? "active" : ""}
          onClick={() => setActiveTab("scheduled")}
        >
          Scheduled ({scheduled.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading">Loading drafts...</div>
      ) : (
        <div className="drafts-list">
          {activeTab === "drafts" ? (
            drafts.length === 0 ? (
              <div className="empty-state">
                <p>No drafts yet</p>
                <Link to="/feed">Create your first post</Link>
              </div>
            ) : (
              drafts.map((draft) => <DraftCard key={draft._id} draft={draft} />)
            )
          ) : scheduled.length === 0 ? (
            <div className="empty-state">
              <p>No scheduled posts</p>
            </div>
          ) : (
            scheduled.map((draft) => (
              <DraftCard key={draft._id} draft={draft} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DraftsPage;
